import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, GhostButton, PillBadge } from "../components/Primitives";
import { RefreshCw, Database, Clock, AlertTriangle } from "lucide-react";

// ─── Helpers ────────────────────────────────────────────────────────
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (err) { return iso; }
}

function relTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = then - Date.now();
  const absSec = Math.abs(diffMs) / 1000;
  if (absSec < 60) return diffMs < 0 ? "just now" : "in <1m";
  const abs = absSec;
  const units = [
    [60 * 60 * 24 * 365, "y"],
    [60 * 60 * 24 * 30,  "mo"],
    [60 * 60 * 24 * 7,   "w"],
    [60 * 60 * 24,       "d"],
    [60 * 60,            "h"],
    [60,                 "m"],
  ];
  for (const [s, label] of units) {
    if (abs >= s) {
      const n = Math.floor(abs / s);
      return diffMs < 0 ? `${n}${label} ago` : `in ${n}${label}`;
    }
  }
  return null;
}

function retentionLabel(days) {
  if (days == null) return "On timestamp";
  if (days % 365 === 0) return `${days / 365}y`;
  if (days % 30 === 0) return `${days / 30}mo`;
  return `${days}d`;
}

// ─── Page ───────────────────────────────────────────────────────────
export default function DataRetention() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await api.get("/admin/data-retention");
      setData(r.data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scope = data?.scope || "agency";
  const totalRows = (data?.policies || []).reduce((sum, p) => sum + (p.total || 0), 0);
  const totalPruning = (data?.policies || []).reduce((sum, p) => sum + (p.subject_to_ttl || 0), 0);

  return (
    <div data-testid="data-retention-page">
      <PageHeader
        title="Data Retention"
        subtitle="MongoDB TTL policies for self-pruning audit and notification tables. Rows expire automatically — no manual cleanup required."
        action={
          <GhostButton onClick={() => load(true)} disabled={refreshing} data-testid="data-retention-refresh">
            <RefreshCw className={`size-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </GhostButton>
        }
      />

      <div className="p-8 space-y-6">
        {/* Scope + roll-up strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Scope</div>
            <div className="font-display text-2xl font-black text-white mt-1" data-testid="data-retention-scope">
              {scope === "global" ? "Global (all agencies)" : "Your agency"}
            </div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Total rows tracked</div>
            <div className="font-display text-2xl font-black text-white mt-1" data-testid="data-retention-total">{totalRows.toLocaleString()}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Subject to TTL</div>
            <div className="font-display text-2xl font-black text-[#34C759] mt-1" data-testid="data-retention-pruning">{totalPruning.toLocaleString()}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Last refreshed</div>
            <div className="font-display text-lg font-black text-white mt-1">
              {data?.generated_at ? fmtDateTime(data.generated_at) : "—"}
            </div>
          </div>
        </div>

        {/* Policy table */}
        <div className="border border-[#27272A] bg-[#0A0A0A]">
          <div className="border-b border-[#27272A] px-4 py-3 flex items-center gap-2">
            <Database className="size-4 text-zinc-400" />
            <div className="label-mute">Retention policies</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="data-retention-table">
                <thead>
                  <tr className="text-left uppercase tracking-widest text-[10px] text-zinc-500 border-b border-[#27272A]">
                    <th className="px-4 py-3">Collection</th>
                    <th className="px-4 py-3">Retention</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right">Subject to TTL</th>
                    <th className="px-4 py-3 text-right">Kept indefinitely</th>
                    <th className="px-4 py-3">Oldest row</th>
                    <th className="px-4 py-3">Next reap</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.policies || []).map((p) => {
                    const oldestRel = relTime(p.oldest_iso);
                    const reapRel   = relTime(p.next_reap_iso);
                    const isTail = p.partial && p.not_subject > 0;
                    // A row is potentially concerning if there's a big pile
                    // NOT subject to TTL and the policy is a partial one.
                    return (
                      <tr key={p.collection} className="border-b border-[#1c1c1e] hover:bg-[#141414] transition-colors" data-testid={`data-retention-row-${p.collection}`}>
                        <td className="px-4 py-3 align-top">
                          <div className="font-mono text-zinc-100 text-xs">{p.collection}</div>
                          <div className="text-zinc-400 text-xs mt-0.5">{p.label}</div>
                          <div className="text-zinc-500 text-[11px] mt-1">{p.purpose}</div>
                          <div className="text-zinc-500 text-[10px] uppercase tracking-widest mt-2">
                            <Clock className="size-3 inline mr-1 -mt-0.5" />
                            <span className="font-mono">{p.ttl_field}</span>
                            {p.partial && <PillBadge tone="blue" className="ml-2">Partial</PillBadge>}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="font-display text-lg font-black text-white">{retentionLabel(p.retention_days)}</div>
                          {p.retention_days == null && (
                            <div className="text-zinc-500 text-[10px] mt-0.5">Expires when the field&rsquo;s own timestamp elapses.</div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <div className="font-mono text-zinc-100">{(p.total || 0).toLocaleString()}</div>
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <div className="font-mono text-[#34C759]">{(p.subject_to_ttl || 0).toLocaleString()}</div>
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <div className={`font-mono ${isTail ? "text-[#FF9F0A]" : "text-zinc-400"}`}>
                            {(p.not_subject || 0).toLocaleString()}
                          </div>
                          {isTail && (
                            <div className="text-zinc-500 text-[10px] mt-0.5 flex items-center justify-end gap-1">
                              <AlertTriangle className="size-3" />
                              Undelivered / open
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-zinc-200 text-xs">{fmtDateTime(p.oldest_iso)}</div>
                          {oldestRel && <div className="text-zinc-500 text-[10px] mt-0.5">{oldestRel}</div>}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-zinc-200 text-xs">{fmtDateTime(p.next_reap_iso)}</div>
                          {reapRel && <div className="text-zinc-500 text-[10px] mt-0.5">{reapRel}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border border-[#27272A] bg-[#0A0A0A] p-4 text-xs text-zinc-400 leading-relaxed">
          <div className="uppercase tracking-widest text-zinc-500 text-[10px] mb-2">How TTL works</div>
          MongoDB&rsquo;s TTL monitor runs approximately every 60 seconds. Rows whose
          <span className="font-mono text-zinc-300 mx-1">TTL field</span> is older than the retention window are removed automatically —
          typically within a minute of expiry. Rows on <span className="font-mono text-zinc-300 mx-1">Partial</span> indexes only
          count toward TTL when the field is a real BSON date; nulls / strings are exempt so that undelivered work isn&rsquo;t lost.
        </div>
      </div>
    </div>
  );
}
